import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import PublicLayout from "@/components/layout/PublicLayout";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { CLIENT_NAV, SPECIALIST_NAV, ADMIN_NAV } from "@/data/nav";

// Public
import Home from "@/pages/public/Home";
import Servizi from "@/pages/public/Servizi";
import ServiceDetail from "@/pages/public/ServiceDetail";
import WalletsIndex from "@/pages/public/WalletsIndex";
import WalletDetail from "@/pages/public/WalletDetail";
import Criptovalute from "@/pages/public/Criptovalute";
import ComeFunziona from "@/pages/public/ComeFunziona";
import Prezzi from "@/pages/public/Prezzi";
import ChiSiamo from "@/pages/public/ChiSiamo";
import Team from "@/pages/public/Team";
import Recensioni from "@/pages/public/Recensioni";
import CasiDiSuccesso from "@/pages/public/CasiDiSuccesso";
import Blog from "@/pages/public/Blog";
import BlogDetail from "@/pages/public/BlogDetail";
import KnowledgeBase from "@/pages/public/KnowledgeBase";
import Faq from "@/pages/public/Faq";
import Contatti from "@/pages/public/Contatti";
import Sicurezza from "@/pages/public/Sicurezza";
import Reclami from "@/pages/public/Reclami";
import LegalPage from "@/pages/public/LegalPage";

// Auth
import Login from "@/pages/auth/Login";
import Registrazione from "@/pages/auth/Registrazione";
import PasswordDimenticata from "@/pages/auth/PasswordDimenticata";
import ResetPassword from "@/pages/auth/ResetPassword";
import ForcedChangePassword from "@/pages/auth/ForcedChangePassword";

// Intake
import AvviaRecupero from "@/pages/intake/AvviaRecupero";

// Portal
import Overview from "@/pages/portal/Overview";
import MyCases from "@/pages/portal/MyCases";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import Notifications from "@/pages/portal/Notifications";
import Profile from "@/pages/portal/Profile";
import Security from "@/pages/portal/Security";
import Deposit from "@/pages/portal/Deposit";
import Withdraw from "@/pages/portal/Withdraw";
import Transactions from "@/pages/portal/Transactions";

// Shared
import CaseDetail from "@/pages/shared/CaseDetail";

// Specialist
import SpecialistQueue from "@/pages/specialist/SpecialistQueue";

// Admin
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminCases from "@/pages/admin/AdminCases";
import AdminSpecialists from "@/pages/admin/AdminSpecialists";
import AdminContent from "@/pages/admin/AdminContent";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminDeposits from "@/pages/admin/AdminDeposits";
import AdminTransactions from "@/pages/admin/AdminTransactions";

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public site */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/servizi" element={<Servizi />} />
          <Route path="/servizi/:slug" element={<ServiceDetail />} />
          <Route path="/wallet" element={<WalletsIndex />} />
          <Route path="/wallet/:slug" element={<WalletDetail />} />
          <Route path="/criptovalute" element={<Criptovalute />} />
          <Route path="/come-funziona" element={<ComeFunziona />} />
          <Route path="/prezzi" element={<Prezzi />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/team" element={<Team />} />
          <Route path="/recensioni" element={<Recensioni />} />
          <Route path="/casi-di-successo" element={<CasiDiSuccesso />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/sicurezza" element={<Sicurezza />} />
          <Route path="/reclami" element={<Reclami />} />
          <Route path="/termini" element={<LegalPage page="termini" />} />
          <Route path="/privacy" element={<LegalPage page="privacy" />} />
          <Route path="/cookie-policy" element={<LegalPage page="cookie-policy" />} />
          <Route path="/disclaimer" element={<LegalPage page="disclaimer" />} />
        </Route>

        {/* Intake (own layout / gate) */}
        <Route path="/avvia-recupero" element={<AvviaRecupero />} />

        {/* Auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/registrazione" element={<Registrazione />} />
        <Route path="/password-dimenticata" element={<PasswordDimenticata />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/cambia-password" element={<ForcedChangePassword />} />

        {/* Client portal */}
        <Route element={<ProtectedRoute roles={["client"]}><DashboardLayout nav={CLIENT_NAV} title={{ it: "Portale cliente", en: "Client portal", de: "Kundenportal" }} /></ProtectedRoute>}>
          <Route path="/portal" element={<Overview />} />
          <Route path="/portal/cases" element={<MyCases />} />
          <Route path="/portal/cases/:id" element={<CaseDetail basePath="/portal/cases" />} />
          <Route path="/portal/messages" element={<PortalMessages />} />
          <Route path="/portal/documents" element={<PortalDocuments />} />
          <Route path="/portal/deposit" element={<Deposit />} />
          <Route path="/portal/withdraw" element={<Withdraw />} />
          <Route path="/portal/transactions" element={<Transactions />} />
          <Route path="/portal/notifications" element={<Notifications />} />
          <Route path="/portal/profile" element={<Profile />} />
          <Route path="/portal/security" element={<Security />} />
        </Route>

        {/* Specialist */}
        <Route element={<ProtectedRoute roles={["specialist"]}><DashboardLayout nav={SPECIALIST_NAV} title={{ it: "Workspace specialista", en: "Specialist workspace", de: "Spezialisten-Arbeitsbereich" }} /></ProtectedRoute>}>
          <Route path="/specialist" element={<SpecialistQueue />} />
          <Route path="/specialist/cases/:id" element={<CaseDetail basePath="/specialist" />} />
        </Route>

        {/* Admin */}
        <Route element={<ProtectedRoute roles={["admin", "super_admin"]}><DashboardLayout nav={ADMIN_NAV} title={{ it: "Console amministrazione", en: "Admin console", de: "Verwaltungskonsole" }} /></ProtectedRoute>}>
          <Route path="/admin" element={<AdminOverview />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/clients" element={<AdminClients />} />
          <Route path="/admin/clients/:id" element={<AdminClientDetail />} />
          <Route path="/admin/cases" element={<AdminCases />} />
          <Route path="/admin/cases/:id" element={<CaseDetail basePath="/admin/cases" />} />
          <Route path="/admin/specialists" element={<AdminSpecialists />} />
          <Route path="/admin/deposits" element={<AdminDeposits />} />
          <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
          <Route path="/admin/transactions" element={<AdminTransactions />} />
          <Route path="/admin/content" element={<AdminContent />} />
          <Route path="/admin/audit" element={<AdminAudit />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
